import { useCallback, useEffect, useRef, useState } from 'react';
import { useMusicState } from './storeHooks/useMusicState';
import useAlbumData from './useAlbumData';

// Global flag to prevent duplicate initial loads across components
let globalWisdomInitialLoadTriggered = false;

interface UseWisdomInfiniteScrollOptions {
  rootMargin?: string;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
  genre?: string;
  search?: string;
}

interface UseWisdomInfiniteScrollReturn {
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  reset: () => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  albums: any[]; // Using the store data
}

/**
 * Custom hook for managing infinite scroll of audiobooks albums using the Redux store
 * This ensures both Audiobook and WisdomPage components share the same data
 */
export const useWisdomInfiniteScroll = (
  options: UseWisdomInfiniteScrollOptions = {},
): UseWisdomInfiniteScrollReturn => {
  const { rootMargin = '200px', scrollContainerRef, enabled = true, genre, search } = options;

  // Get store state and actions
  const {
    wisdomPlaylist: albums,
    appendWisdomPlaylist,
    setWisdomPlaylist,
  } = useMusicState();

  const { loadWisdomAlbumsPaginated } = useAlbumData();

  // Local state for infinite scroll
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs for managing state and observer
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const initialLoadTriggered = useRef(false);

  // Load more function
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await loadWisdomAlbumsPaginated(12, offsetRef.current, genre, search);

      if (offsetRef.current === 0) {
        // Initial load - replace items
        setWisdomPlaylist(result?.items || []);
      } else {
        // Subsequent loads - append items
        appendWisdomPlaylist(result?.items || []);
      }

      const hasNext = result?.hasNext || false;
      setHasMore(hasNext);
      offsetRef.current += result?.items?.length || 0;

      // Set initial load done after first successful load
      if (!initialLoadDone) {
        setInitialLoadDone(true);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load more items';
      setError(errorMessage);
      console.error('Error loading more audiobooks items:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [
    loadWisdomAlbumsPaginated,
    hasMore,
    appendWisdomPlaylist,
    setWisdomPlaylist,
    initialLoadDone,
  ]);

  // Reset function
  const reset = useCallback(() => {
    setWisdomPlaylist([]);
    offsetRef.current = 0;
    setHasMore(true);
    setError(null);
    setLoading(false);
    loadingRef.current = false;
    setInitialLoadDone(false);
    initialLoadTriggered.current = false;
  }, [setWisdomPlaylist]);

  // Reset data when genre or search changes and trigger new load
  useEffect(() => {
    console.log('Audiobook genre/search changed, resetting data and triggering load:', { genre, search });

      // Reset the data
      setWisdomPlaylist([]);
      offsetRef.current = 0;
      setHasMore(true);
      setError(null);
      setInitialLoadDone(false);
      initialLoadTriggered.current = false;
      globalWisdomInitialLoadTriggered = false;

      // Trigger immediate load with selected genre
      if (enabled) {
        console.log('Triggering loadMore for audiobooks genre change');
        const triggerLoad = async () => {
          if (!loadingRef.current) {
            loadingRef.current = true;
            setLoading(true);
            setError(null);

            try {
              const result = await loadWisdomAlbumsPaginated(12, 0, genre, search);
              console.log('Audiobook API result for genre/search change:', { result, genre, search });

              setWisdomPlaylist(result?.items || []);
              const hasNext = result?.hasNext || false;
              setHasMore(hasNext);
              offsetRef.current = (result?.items || []).length;
              setInitialLoadDone(true);
              globalWisdomInitialLoadTriggered = true;
            } catch (err) {
              console.error('Error loading audiobooks albums for genre:', err);
              setError(err instanceof Error ? err.message : 'Failed to load albums');
              setHasMore(false);
            } finally {
              setLoading(false);
              loadingRef.current = false;
            }
          }
        };

        triggerLoad();
      } else {
        console.log('Not triggering loadMore - enabled is false');
      }
  }, [genre, search, enabled, loadWisdomAlbumsPaginated]);





  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (!enabled || !hasMore || !sentinelRef.current) return;

    // Disconnect any existing observer first
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Determine the root element for intersection observer
    // If scrollContainerRef is provided, use it; otherwise use null (viewport)
    const rootElement = scrollContainerRef?.current || null;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loadingRef.current && hasMore) {
          loadMore();
        }
      },
      {
        root: rootElement,
        rootMargin,
        threshold: 0.1,
      },
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    enabled,
    hasMore,
    initialLoadDone,
    loadMore,
    rootMargin,
    scrollContainerRef,
  ]);

  // Initial load effect - load data once globally, not per component
  useEffect(() => {
    if (initialLoadTriggered.current) return;

    // Check if any component has already triggered initial load
    if (globalWisdomInitialLoadTriggered) {
      // If data was already loaded globally, just sync local state
      if (albums.length > 0 && !initialLoadDone) {
        setInitialLoadDone(true);
        offsetRef.current = albums.length;
        initialLoadTriggered.current = true;
      }
      return;
    }

    // Only the first component to mount will trigger the initial load
    if (!initialLoadDone && albums.length === 0) {
      globalWisdomInitialLoadTriggered = true;
      initialLoadTriggered.current = true;
      // Set loading to true immediately for initial load to show skeleton
      setLoading(true);
      loadingRef.current = true;
      loadMore(); // Enable initial load - loadMore will set initialLoadDone after completion
    } else if (albums.length > 0 && !initialLoadDone) {
      // If albums already exist in store, sync the offset
      initialLoadTriggered.current = true;
      setInitialLoadDone(true);
      offsetRef.current = albums.length;
    }
  }, []); // Only run once on mount

  return {
    loading,
    hasMore,
    error,
    loadMore,
    reset,
    sentinelRef,
    albums,
  };
};

export default useWisdomInfiniteScroll;
